import { useCallback, useState } from 'react'
import { Box, Flex } from '@npco/zeller-design-system'
import { ModalBasic } from 'design-system/Components/Modal/ModalBasic/ModalBasic'
import { useSaveOnboarding } from 'features/OnboardingApp/shared/useSaveOnboarding'

import { rvIsUserSavingOnboarding } from 'graphql/reactiveVariables'
import { useLogout } from 'hooks/useLogout'
import { MY_ZELLER_URL } from 'const/externalLinks'
import { translate } from 'utils/translations'
import { SESSION_STORAGE_KEYS } from 'services/sessionStorage/keys'
import { clearSessionStorage } from 'services/sessionStorage/utils'
import { ModalDescription } from 'components/Modal/ModalElements/ModalElements'
import { SpinnerWrapped } from 'components/Spinner'
import { component, page } from 'translations'

import {
  StyledCancelButton,
  StyledConfirmButton,
  StyledRedirectButton,
} from './SaveAndCloseModal.styled'

interface Props {
  toggleButton: React.ReactElement | null
  isModalOpen: boolean
  closeModal: () => void
}

export const SaveAndCloseModal = ({
  toggleButton,
  isModalOpen,
  closeModal,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const { saveOnboarding } = useSaveOnboarding()

  const { logout } = useLogout()

  const onSubmit = useCallback(async () => {
    if (isLoading) {
      return
    }
    setIsLoading(true)
    await saveOnboarding()

    logout()
  }, [saveOnboarding, logout, isLoading])

  const handleRedirectionLinkClick = useCallback(async () => {
    setIsLoading(true)
    await saveOnboarding()

    clearSessionStorage(SESSION_STORAGE_KEYS.PREVIOUS_PATH)
    window.location.replace(MY_ZELLER_URL)
  }, [saveOnboarding])

  return (
    <>
      <Flex justifyContent="center">{toggleButton}</Flex>
      <ModalBasic
        isOpen={isModalOpen}
        onCancel={closeModal}
        title={page.onboarding.saveAndClose.title}
      >
        <Box>
          <ModalDescription>
            <Box width={['100%', '28rem']}>
              {isLoading ? (
                <SpinnerWrapped justifyContent="center" />
              ) : (
                <>
                  {page.onboarding.saveAndClose.description}&nbsp;
                  <StyledRedirectButton
                    type="button"
                    onClick={handleRedirectionLinkClick}
                  >
                    {component.saveOnboardingModal.linkLabel}
                  </StyledRedirectButton>
                </>
              )}
            </Box>
          </ModalDescription>
          <Flex
            flexDirection={['column', 'row']}
            justifyContent="space-between"
          >
            <StyledCancelButton
              disabled={isLoading}
              onClick={() => {
                rvIsUserSavingOnboarding(false)
                closeModal()
              }}
            >
              {translate('shared.cancel')}
            </StyledCancelButton>
            <StyledConfirmButton
              disabled={isLoading}
              onClick={() => onSubmit().then(closeModal)}
            >
              {translate('page.onboarding.saveAndClose.saveAndLogout')}
            </StyledConfirmButton>
          </Flex>
        </Box>
      </ModalBasic>
    </>
  )
}
